/**
 * Victory.js Zoom Hook
 * 
 * Reusable hook that provides consistent zoom functionality for Victory.js charts.
 * Handles zoom state management, domain changes, zoom factor calculations,
 * and provides the appropriate container component.
 */

import { useState, useCallback, useMemo } from 'react';
import { VictoryZoomContainer } from 'victory';

export interface UseVictoryZoomOptions {
  /** Whether to enable zoom functionality (default: true) */
  enableZoom?: boolean;
  /** Minimum zoom level (default: 0.1 for 10x zoom in) */
  minimumZoom?: number;
  /** Maximum zoom level (default: 10 for 10x zoom out) */
  maximumZoom?: number;
  /** Whether to allow panning (default: true) */
  allowPan?: boolean;
  /** Whether to allow zooming (default: true) */
  allowZoom?: boolean;
}

export interface UseVictoryZoomReturn {
  /** Victory container component configured for zoom */
  zoomContainer: JSX.Element | undefined;
  /** Current zoom factor (1 = no zoom, 2 = 2x zoom, etc.) */
  zoomFactor: number;
  /** Formatted zoom display text */
  zoomDisplayText: string;
  /** Whether currently zoomed (factor > 1.1) */
  isZoomed: boolean;
  /** Function to reset zoom state to initial values */
  resetZoom: () => void;
  /** Raw zoom domain state for advanced usage */
  zoomState: {
    initialVictoryDomain: number[] | null;
    zoomedXDomain: number[] | null;
  };
}

/**
 * Custom hook for Victory.js zoom functionality
 */
export const useVictoryZoom = (options: UseVictoryZoomOptions = {}): UseVictoryZoomReturn => {
  const {
    enableZoom = true,
    minimumZoom = 0.1,
    maximumZoom = 10,
    allowPan = true,
    allowZoom = true,
  } = options;

  // Zoom state management
  const [initialVictoryDomain, setInitialVictoryDomain] = useState<number[] | null>(null);
  const [zoomedXDomain, setZoomedXDomain] = useState<number[] | null>(null);

  // Handle zoom domain changes from Victory
  const onDomainChange = useCallback((domain: any) => {
    if (!domain || !domain.x) return;
    
    if (domain.x[0] == null || domain.x[1] == null) return;
    
    // Victory might give us different formats - handle both
    let numericDomain: number[];
    
    if (typeof domain.x[0] === 'number') {
      if (domain.x[0] > 1000000000) {
        numericDomain = [domain.x[0], domain.x[1]];
      } else {
        numericDomain = [domain.x[0], domain.x[1]];
      }
    } else {
      const timestamp1 = new Date(domain.x[0]).getTime();
      const timestamp2 = new Date(domain.x[1]).getTime();
      
      if (isNaN(timestamp1) || isNaN(timestamp2)) return;
      
      numericDomain = [timestamp1, timestamp2];
    }
    
    if (!initialVictoryDomain) {
      setInitialVictoryDomain(numericDomain);
      setZoomedXDomain(numericDomain);
    } else {
      setZoomedXDomain(numericDomain);
    }
  }, [initialVictoryDomain]);

  // Calculate zoom factor
  const zoomFactor = useMemo(() => {
    if (!enableZoom || !initialVictoryDomain || !zoomedXDomain) return 1;
    
    try {
      const totalRange = initialVictoryDomain[1] - initialVictoryDomain[0];
      const currentRange = zoomedXDomain[1] - zoomedXDomain[0];
      
      // Prevent division by zero
      if (currentRange <= 0 || totalRange <= 0) return 1;
      
      // Calculate factor
      const factor = totalRange / currentRange;
      
      return factor < 3 ? Math.round(factor * 10) / 10 : Math.round(factor);
    } catch (error) {
      console.warn('Error calculating zoom factor:', error);
      return 1;
    }
  }, [enableZoom, initialVictoryDomain, zoomedXDomain]);

  // Create Victory zoom container
  const zoomContainer = useMemo(() => {
    if (!enableZoom) return undefined;
    
    return (
      <VictoryZoomContainer
        zoomDimension="x"
        onZoomDomainChange={onDomainChange}
        minimumZoom={{ x: minimumZoom }}
        allowPan={allowPan}
        allowZoom={allowZoom}
      />
    );
  }, [enableZoom, onDomainChange, minimumZoom, allowPan, allowZoom]);

  // Determine if currently zoomed
  const isZoomed = zoomFactor > 1.1;

  // Generate display text
  const zoomDisplayText = useMemo(() => {
    if (!enableZoom) return '';
    
    if (isZoomed) {
      return `${zoomFactor}x zoom`;
    } else {
      return 'No zoom';
    }
  }, [enableZoom, zoomFactor, isZoomed]);

  // Reset zoom state to initial values
  const resetZoom = useCallback(() => {
    setInitialVictoryDomain(null);
    setZoomedXDomain(null);
  }, []);

  return {
    zoomContainer,
    zoomFactor,
    zoomDisplayText,
    isZoomed,
    resetZoom,
    zoomState: {
      initialVictoryDomain,
      zoomedXDomain,
    },
  };
};

export default useVictoryZoom;
